import type { Pet } from '../pet';
import { getTimeUntilAwake } from '../pet/pet.ruleset';
import { Inventory } from '.';
import { visitSchema } from '../../visit/visit.schema';
import { createScheduledActions, SB } from '@play-co/replicant';
import { extractScheduledActionHandlers } from '../../scheduledActions/utils';
import { NotificationSettings } from 'src/replicant/chatbot/chatbot.ruleset';
import { Locale } from 'src/replicant/ruleset/locale';
import { levelSchema } from '../level/level.schema';
import { encyclopediaSchema } from '../../encyclopedia/encyclopedia.schema';
import { userSettingsSchema } from '../../state/settings';
import { Feature } from 'src/replicant/analytics';
import {
  getChatbotStaticMessage,
  sendChatbotMessage,
  generateReplicantChatbotPayload,
} from 'src/replicant/chatbot/chatbotMessage';
import { getLocaleConfig } from 'src/replicant/creatives/text';

export const inventoryScheduledActions = (
  Inventory: Inventory,
  { Pet }: Inventory['$DEPENDENCIES'],
) => {
  const stateSchema = SB.object({
    petRaised: SB.number(),
    highestPetAge: SB.number(),
    pet: Pet.schema.pet,
    settings: userSettingsSchema,
    inventory: Inventory.schema.inventory,
    visit: visitSchema.visit,
    level: levelSchema.level,
    encyclopedia: encyclopediaSchema.encyclopedia,
    platform: SB.string().optional(),
  });
  const apiSchema = {
    energyRefilled: SB.object({ locale: SB.tuple(['en', 'ja']) }),
  };

  const actions = createScheduledActions(
    stateSchema,
    apiSchema,
  )({
    energyRefilled: async (state, { locale }, api) => {
      const { receiveMessages } = state.settings;

      if (receiveMessages !== NotificationSettings.On) return;

      const payload = generateReplicantChatbotPayload({
        feature: Feature.NOTIFICATION._,
        $subFeature: Feature.NOTIFICATION.ENERGY_FULL,
      });

      const { getCurrentEvolution } = Pet.getters(state);
      const { getCurrencies } = Inventory.getters(state);
      const species = getCurrentEvolution().species;
      const { points, energy, freeGems, paidGems } = getCurrencies(
        api.date.now(),
      );
      const analyticsUserProperties = {
        coins: points,
        energy,
        freeGems,
        paidGems,
        totalGems: freeGems + paidGems,
      };

      // LINE Chatbot Message
      // const message = getChatbotFlexMessage({
      //   template: 'fullenergy',
      //   payload,
      //   data: {
      //     locale,
      //     species: getCurrentEvolution().species,
      //     petName: state.pet.name,
      //   },
      //   analyticsUserProperties,
      // });
      // api.chatbot.sendMessage(api.getUserID(), message);

      // WEB Chatbot Message
      const imageId = `static/${species}/fullenergy`;
      const message = await getChatbotStaticMessage({
        state,
        api,
        imageUrl: imageId,
        title: getLocaleConfig(locale).energy.full.text,
        payload: {
          ...payload,
          $creativeAssetID: imageId,
        },
        analyticsUserProperties,
      });
      void sendChatbotMessage(api, api.getUserID(), message, state.platform);
    },
  });

  return {
    schema: apiSchema,
    actions: extractScheduledActionHandlers(actions),
  };
};

export function scheduleEnergyRefilledMessage(
  evolution: Pet.Evolution,
  energy: number,
  timeToFullEnergy: number,
  locale: Locale,
  state: Record<string, any>,
  api,
) {
  // If energy is greater than 1, don't schedule energy refilled message.
  if (energy > 1) {
    return;
  }

  // Time until pet will be awake from scheduled time.
  const timeTillAwake = getTimeUntilAwake(
    evolution,
    api.date.now() + timeToFullEnergy,
    state,
  );

  // Get delay, moving message to the morning if pet's sleeping.
  const delayInMS = timeToFullEnergy + timeTillAwake;

  // Otherwise schedule it.
  api.scheduledActions.schedule.energyRefilled({
    args: { locale },
    notificationId: 'energyRefilled',
    delayInMS,
  });
}
